/**
 * 新版尺码选择器 JavaScript - 性能和无障碍优化版
 *
 * 功能：
 * - 尺码选择和尺码体系切换
 * - 动态尺码转换（鞋类产品）
 * - 购物车AJAX提交（已整合）
 * - 骨架屏加载（防CLS）
 * - 无障碍支持（ARIA、键盘导航）
 *
 * 调试模式：
 * - 设置 DEBUG_MODE = true 可以在控制台查看详细日志
 * - 生产环境建议设置为 false 以提升性能
 *
 * @version 2.0
 * @date 2025-01-28
 */

(function () {
  "use strict";

  // 🔧 调试模式开关 - 生产环境设置为 false
  const DEBUG_MODE = false;

  // 日志辅助函数
  const log = {
    info: (...args) => DEBUG_MODE && console.log(...args),
    warn: (...args) => DEBUG_MODE && console.warn(...args),
    error: (...args) => console.error(...args), // 错误总是显示
  };

  // 性能优化: 防抖函数
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // 性能优化: 节流函数
  function throttle(func, limit) {
    let inThrottle;
    return function (...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  class SizeSelector {
    constructor(container) {
      this.container = container;
      this.productId = container.dataset.productId;
      this.data = this.loadProductData();

      if (!this.data) return;

      // 初始化尺码转换器
      this.converter = window.sizeConverter || null;

      // 检测产品类型
      this.productType = "OTHER";
      if (this.converter) {
        this.productType = this.converter.detectProductType(this.data.variants);

        // 给容器添加产品类型标记
        this.container.setAttribute("data-product-type", this.productType);

        // 只对鞋类产品进行转换
        if (this.productType === "FOOTWEAR") {
          this.data.variants = this.converter.convertProductVariants(
            this.data.variants,
          );
        }
      }

      // 性能优化: 缓存 DOM 查询
      this.elements = this.cacheElements(container);

      // 性能优化: 缓存表单元素
      this.formElements = null;

      // 强制清除任何预选的尺码（确保用户主动选择）
      const allSizeButtons = this.elements.sizeButtons;
      allSizeButtons.forEach((btn) => {
        btn.classList.remove("is-selected");
        btn.setAttribute("aria-pressed", "false");
      });

      this.state = {
        currentSystem: this.getCurrentSystem(),
        selectedSize: "", // 强制为空，不自动选中
        selectedVariant: null, // 强制为 null，不自动选中
        isUpdating: false,
      };

      // 性能优化: 创建防抖版本的更新函数
      this.debouncedUpdateForm = debounce(() => this.updateProductForm(), 50);

      this.init();
    }

    // 性能优化: 统一缓存 DOM 元素
    cacheElements(container) {
      return {
        tabs: container.querySelectorAll("[data-size-tab]"),
        panels: container.querySelectorAll("[data-size-panel]"),
        sizeButtons: container.querySelectorAll("[data-size-btn]"),
        statusValue: container.querySelector("[data-size-status]"),
        tablist:
          container.querySelector('[role="tablist"]') ||
          container.querySelector(".t4s-size-selector__tabs"),
        lowStockAlert: container.querySelector("[data-low-stock-alert]"),
        lowStockText: container.querySelector("[data-low-stock-text]"),
      };
    }

    loadProductData() {
      const dataScript = document.querySelector(
        `[data-size-selector-variants="${this.productId}"]`,
      );
      if (!dataScript) return null;

      try {
        return JSON.parse(dataScript.textContent);
      } catch (e) {
        log.error("尺码选择器: 数据解析失败", e);
        return null;
      }
    }

    getCurrentSystem() {
      const activeTab = this.container.querySelector(
        ".t4s-size-selector__tab.is-active",
      );
      return activeTab ? activeTab.dataset.sizeTab : "US-M";
    }

    getCurrentSize() {
      const selectedButton = this.container.querySelector(
        ".t4s-size-selector__size-btn.is-selected",
      );
      return selectedButton ? selectedButton.dataset.sizeValue : "";
    }

    getCurrentVariant() {
      const currentId = this.data.currentVariantId;
      return this.data.variants.find((v) => v.id === currentId) || null;
    }

    getStatusSystemLabel(system) {
      const key = String(system || "").toUpperCase();
      if (key === "US-M") return "US-MEN";
      if (key === "US-W") return "US-WOMEN";
      return system;
    }

    init() {
      // 无障碍: 设置 ARIA 属性
      this.setupAccessibility();

      this.bindEvents();

      // 如果是鞋类产品,立即更新当前标签的尺码显示
      if (this.productType === "FOOTWEAR" && this.converter) {
        this.updateSizeButtonsForSystem(this.state.currentSystem);

        // 显示尺码体系标签页(鞋类产品)
        if (this.elements.tablist) {
          this.elements.tablist.style.display = "";
        }
      }

      this.updateStatusText();
      this.initSelectorTapData();

      // ⚠️ 重要：初始化时立即更新表单状态
      // 确保页面加载时按钮状态正确
      if (!this.state.selectedVariant) {
        // 如果没有预选的变体，确保按钮被禁用
        this.updateProductForm();
      }

      // 🎨 性能优化：移除骨架屏，显示真实内容，防止CLS
      this.removeSkeleton();

      // 🛒 初始化购物车提交功能
      this.initCartSubmission();
    }

    /**
     * 移除骨架屏并显示真实内容
     * 使用原位置过渡，防止CLS（累积布局偏移）
     */
    removeSkeleton() {
      const skeleton = this.container.querySelector("[data-skeleton]");
      const content = this.container.querySelector("[data-size-content]");

      if (!skeleton || !content) return;

      // 使用requestAnimationFrame优化性能
      requestAnimationFrame(() => {
        // 骨架屏淡出
        skeleton.classList.add("fade-out");

        // 真实内容淡入（内容一直存在，只是透明度变化）
        content.classList.add("fade-in");

        // 动画完成后移除骨架屏DOM
        setTimeout(() => {
          skeleton.remove();
          log.info("[尺码选择器] ✅ 骨架屏已移除，内容已显示 (零CLS)");
        }, 300);
      });
    }

    initSelectorTapData() {
      try {
        const currentSystem = localStorage.getItem("wade_global_size_format");

        const isValid = ["US-M", "US-W", "EU", "UK", "CM", "IN"].includes(
          currentSystem,
        );

        if (isValid) {
          const targetTab = this.container.querySelector(
            `.t4s-size-selector__tab[data-size-tab="${currentSystem}"]`,
          );

          if (targetTab && !targetTab.classList.contains("is-active")) {
            targetTab.click();
          }
        } else {
          localStorage.removeItem("wade_global_size_format");
        }
      } catch {}
    }

    // 无障碍: 设置 ARIA 属性和键盘支持
    setupAccessibility() {
      // 为标签页容器设置 role
      if (this.elements.tablist) {
        this.elements.tablist.setAttribute("role", "tablist");
        this.elements.tablist.setAttribute(
          "aria-label",
          "Size System Selector",
        );
      }

      // 为每个标签页设置 ARIA 属性
      this.elements.tabs.forEach((tab, index) => {
        tab.setAttribute("role", "tab");
        tab.setAttribute("aria-controls", `size-panel-${tab.dataset.sizeTab}`);
        tab.setAttribute(
          "tabindex",
          tab.classList.contains("is-active") ? "0" : "-1",
        );

        const isActive = tab.classList.contains("is-active");
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      // 为面板设置 ARIA 属性
      this.elements.panels.forEach((panel) => {
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("id", `size-panel-${panel.dataset.sizePanel}`);
        panel.setAttribute("aria-labelledby", `tab-${panel.dataset.sizePanel}`);

        if (!panel.classList.contains("is-active")) {
          panel.setAttribute("hidden", "");
        }
      });

      // 为尺码按钮添加 ARIA 属性
      this.elements.sizeButtons.forEach((btn) => {
        const isAvailable = btn.dataset.available === "true";
        const size = btn.dataset.sizeValue;

        btn.setAttribute("role", "button");
        btn.setAttribute(
          "aria-label",
          `Size ${size}${isAvailable ? "" : " - Out of stock"}`,
        );

        if (!isAvailable) {
          btn.setAttribute("aria-disabled", "true");
        }

        if (btn.classList.contains("is-selected")) {
          btn.setAttribute("aria-pressed", "true");
        } else {
          btn.setAttribute("aria-pressed", "false");
        }
      });

      // 为状态显示区域设置 ARIA live region
      if (this.elements.statusValue) {
        this.elements.statusValue.setAttribute("role", "status");
        this.elements.statusValue.setAttribute("aria-live", "polite");
        this.elements.statusValue.setAttribute("aria-atomic", "true");
      }
    }

    bindEvents() {
      // 标签页切换 - 点击事件
      this.elements.tabs.forEach((tab) => {
        tab.addEventListener("click", (e) => this.handleTabClick(e));
      });

      // 无障碍: 标签页键盘导航
      this.elements.tablist?.addEventListener("keydown", (e) =>
        this.handleTabKeydown(e),
      );

      // 尺码按钮点击
      this.elements.sizeButtons.forEach((btn) => {
        btn.addEventListener("click", (e) => this.handleSizeClick(e));
      });

      // 无障碍: 尺码按钮键盘导航
      this.elements.sizeButtons.forEach((btn) => {
        btn.addEventListener("keydown", (e) => this.handleSizeButtonKeydown(e));
      });
    }

    // 无障碍: 标签页键盘导航
    handleTabKeydown(e) {
      const tabs = Array.from(this.elements.tabs);
      const currentIndex = tabs.findIndex((tab) => tab === e.target);

      if (currentIndex === -1) return;

      let newIndex = currentIndex;

      switch (e.key) {
        case "ArrowLeft":
        case "ArrowUp":
          e.preventDefault();
          newIndex = currentIndex - 1 < 0 ? tabs.length - 1 : currentIndex - 1;
          break;
        case "ArrowRight":
        case "ArrowDown":
          e.preventDefault();
          newIndex = currentIndex + 1 >= tabs.length ? 0 : currentIndex + 1;
          break;
        case "Home":
          e.preventDefault();
          newIndex = 0;
          break;
        case "End":
          e.preventDefault();
          newIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      // 切换到新标签页
      tabs[newIndex].click();
      tabs[newIndex].focus();
    }

    // 无障碍: 尺码按钮键盘导航
    handleSizeButtonKeydown(e) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.target.click();
      }
    }

    handleTabClick(e) {
      // 防止重复操作
      if (this.state.isUpdating) return;

      e.stopPropagation();

      const tab = e.currentTarget;
      const system = tab.dataset.sizeTab;

      try {
        localStorage.setItem("wade_global_size_format", system);
      } catch {}

      // 如果点击的是当前活动标签,不做任何操作
      if (this.state.currentSystem === system) return;

      this.state.isUpdating = true;

      // 性能优化: 使用 requestAnimationFrame 批量更新 DOM
      requestAnimationFrame(() => {
        // 更新标签页 ARIA 和样式
        this.elements.tabs.forEach((t) => {
          const isActive = t === tab;
          t.classList.toggle("is-active", isActive);
          t.setAttribute("aria-selected", isActive ? "true" : "false");
          t.setAttribute("tabindex", isActive ? "0" : "-1");
        });

        // 更新面板显示和 ARIA
        this.elements.panels.forEach((panel) => {
          const isActive = panel.dataset.sizePanel === system;
          panel.classList.toggle("is-active", isActive);

          if (isActive) {
            panel.removeAttribute("hidden");
          } else {
            panel.setAttribute("hidden", "");
          }
        });

        this.state.currentSystem = system;

        // 只对鞋类产品启用尺码转换和动态更新
        if (this.productType === "FOOTWEAR" && this.converter) {
          this.updateSizeButtonsForSystem(system);
        }

        this.updateStatusText();
        this.updateLowStockAlert();
        this.updateGroupedProductAvailability();
        this.debouncedUpdateForm();

        this.state.isUpdating = false;
      });
    }

    handleSizeClick(e) {
      const button = e.currentTarget;
      const size = button.dataset.sizeValue;
      const variantId = button.dataset.variantId;
      const isAvailable = button.dataset.available === "true";

      // 缺货商品处理
      if (!isAvailable) {
        this.handleOutOfStockClick(button, variantId);
        return;
      }

      // 切换选中状态
      const wasSelected = button.classList.contains("is-selected");

      // 立即更新状态（不使用 requestAnimationFrame 避免延迟）
      if (!wasSelected) {
        this.state.selectedSize = size;
        this.state.selectedVariant = this.data.variants.find(
          (v) => v.id === parseInt(variantId),
        );

        log.info("[尺码选择器] 选中尺码:", size, "变体ID:", variantId);
        log.info("[尺码选择器] 变体对象:", this.state.selectedVariant);
      } else {
        this.state.selectedSize = "";
        this.state.selectedVariant = null;
        log.info("[尺码选择器] 取消选择");
      }

      // 立即更新表单（关键操作，不能延迟）
      this.updateProductForm();

      // 更新库存紧张提示
      this.updateLowStockAlert();

      // 更新分组产品（Style swatch）的库存可用性蒙层
      this.updateGroupedProductAvailability();

      // 视觉更新使用 requestAnimationFrame 优化性能
      requestAnimationFrame(() => {
        // 更新所有按钮状态
        this.elements.sizeButtons.forEach((btn) => {
          btn.classList.remove("is-selected");
          btn.setAttribute("aria-pressed", "false");
        });

        if (!wasSelected) {
          button.classList.add("is-selected");
          button.setAttribute("aria-pressed", "true");

          // 无障碍: 向屏幕阅读器宣布选择
          this.announceSelection(size, this.state.currentSystem);
        } else {
          // 无障碍: 向屏幕阅读器宣布取消选择
          this.announceSelection(null);
        }

        this.updateStatusText();
      });
    }

    // 无障碍: 向屏幕阅读器宣布尺码选择
    announceSelection(size, system) {
      if (!this.elements.statusValue) return;

      const message = size
        ? `Selected size: ${size} ${system || this.state.currentSystem}`
        : "Size selection cleared";

      // ARIA live region 会自动宣布内容变化
      // updateStatusText() 会更新这个区域
    }

    /**
     * 🔥 更新库存紧张提示
     * 当选中的变体库存 < 10 时显示 "Only X left" 提示
     * 使用 inventoryMap（从 Liquid 端注入的真实库存数据）
     */
    updateLowStockAlert() {
      const alertEl = this.elements.lowStockAlert;
      const textEl = this.elements.lowStockText;

      if (!alertEl || !textEl) return;

      // 如果没有选中变体，隐藏提示
      if (!this.state.selectedVariant) {
        alertEl.style.display = "none";
        alertEl.classList.remove("is-visible");
        return;
      }

      const variant = this.state.selectedVariant;
      const variantId = String(variant.id);

      // 从 inventoryMap 获取库存数据（Liquid 端注入的真实数据）
      const inventoryMap = this.data.inventoryMap || {};
      const inventoryInfo = inventoryMap[variantId];

      if (!inventoryInfo) {
        alertEl.style.display = "none";
        alertEl.classList.remove("is-visible");
        return;
      }

      const quantity = inventoryInfo.quantity;
      const management = inventoryInfo.management;

      // 只有在 Shopify 追踪库存 且 库存大于0 且 小于10 时才显示
      if (management === "shopify" && quantity > 0 && quantity < 10) {
        textEl.textContent = `Only ${quantity} left - Order soon!`;
        alertEl.style.display = "";
        // 使用 requestAnimationFrame 触发动画
        requestAnimationFrame(() => {
          alertEl.classList.add("is-visible");
        });
        log.info(`[尺码选择器] 库存紧张提示: Only ${quantity} left`);

        // OpenPanel 追踪：Low Stock Alert Shown（防重复）
        if (this._lastTrackedLowStockVariant !== variantId) {
          this._lastTrackedLowStockVariant = variantId;
          try {
            const sizeOptionIndex = this.data.sizeOptionIndex;
            const optionKey = `option${sizeOptionIndex + 1}`;
            OpenPanel.track("Low Stock Alert Shown", {
              product_id: this.data.productId,
              product_handle: this.data.product?.handle || null,
              product_title: this.data.product?.title || null,
              variant_id: variant.id,
              size: variant[optionKey] || null,
              inventory_quantity: quantity,
              source: "pdp_size_selector",
            });
          } catch (e) {}
        }
      } else {
        alertEl.style.display = "none";
        alertEl.classList.remove("is-visible");
      }
    }

    /**
     * 🎨 更新分组产品（Style swatch）的库存可用性
     * 当用户选中一个尺码后，检查各分组产品在该尺码下是否有库存
     * 没库存的加灰色斜线蒙层
     */
    updateGroupedProductAvailability() {
      // 获取分组产品库存数据
      const dataScript = document.querySelector("[data-grouped-inventory]");
      if (!dataScript) return;

      let groupedData;
      try {
        groupedData = JSON.parse(dataScript.textContent);
      } catch (e) {
        log.error("[尺码选择器] 分组产品数据解析失败", e);
        return;
      }

      // 获取所有分组产品 swatch items
      const styleItems = document.querySelectorAll(
        ".t4s-pr-choose__item[data-style-product-id]",
      );
      if (styleItems.length === 0) return;

      // 如果没有选中变体，清除所有蒙层
      if (!this.state.selectedVariant) {
        styleItems.forEach((item) => {
          item.classList.remove("is-size-unavailable");
        });
        return;
      }

      // 获取当前选中的原始尺码值（option1 中的尺码值）
      const selectedVariant = this.state.selectedVariant;
      const sizeOptionIndex = this.data.sizeOptionIndex;
      const optionKey = `option${sizeOptionIndex + 1}`;
      const selectedOriginalSize = selectedVariant[optionKey];

      log.info(
        "[尺码选择器] 检查分组产品库存, 选中尺码:",
        selectedOriginalSize,
      );

      // 遍历每个分组产品 swatch
      styleItems.forEach((item) => {
        const productId = item.dataset.styleProductId;
        const productData = groupedData[productId];

        if (!productData) return;

        // 检查该产品在选中尺码下是否有可用库存
        let hasSizeAvailable = false;
        const sizeInventory = productData.sizeInventory || {};

        for (const variantId in sizeInventory) {
          const variantInfo = sizeInventory[variantId];
          if (
            variantInfo.size === selectedOriginalSize &&
            variantInfo.available
          ) {
            hasSizeAvailable = true;
            break;
          }
        }

        // 切换蒙层 class
        if (hasSizeAvailable) {
          item.classList.remove("is-size-unavailable");
        } else {
          item.classList.add("is-size-unavailable");
        }
      });
    }

    handleOutOfStockClick(button, variantId) {
      // 获取尺码信息
      const size = button.dataset.sizeValue;
      const system = this.state.currentSystem;

      // 获取变体信息
      const variant = this.data.variants.find(
        (v) => v.id === parseInt(variantId),
      );
      if (!variant) return;

      // 触发自定义弹窗,使用 Notify Me API
      // 只传递 size，system 在 showNotifyMeModal 中格式化
      this.showNotifyMeModal({
        product: this.data.product,
        variant: variant,
        selectedSize: size,
        system: system,
      });
    }

    resolveNotifyButton(product) {
      const scoped = this.container?.querySelector("[data-notify-stock-btn]");
      if (scoped) return scoped;

      const productId = product?.id != null ? String(product.id) : "";
      if (productId) {
        const buttons = document.querySelectorAll("[data-notify-stock-btn]");
        for (const btn of buttons) {
          const id =
            btn.dataset.productId || btn.getAttribute("data-product-id") || "";
          if (String(id) === productId) return btn;
        }
      }

      return document.querySelector("[data-notify-stock-btn]");
    }

    buildSizeFilterValue(selectedSize, sizeSystem, variant) {
      const converter = this.converter || window.sizeConverter;
      if (
        selectedSize &&
        sizeSystem &&
        converter &&
        typeof converter.convertSize === "function"
      ) {
        const converted = converter.convertSize(sizeSystem, selectedSize);
        if (converted) {
          const parts = [];
          if (converted["US-M"]) parts.push(`US-M ${converted["US-M"]}`);
          if (converted["US-W"]) parts.push(`US-W ${converted["US-W"]}`);
          if (converted["EU"]) parts.push(`EU ${converted["EU"]}`);
          if (converted["UK"]) parts.push(`UK ${converted["UK"]}`);
          if (converted["CM"]) parts.push(`${converted["CM"]}cm`);
          if (converted["IN"]) parts.push(`${converted["IN"]}"`);
          if (parts.length) return parts.join(" / ");
        }
      }

      const options = [variant?.option1, variant?.option2, variant?.option3]
        .map((value) => String(value || "").trim())
        .filter((value) => /\d/.test(value));
      return options[0] || String(selectedSize || "").trim();
    }

    /**
     * 显示 Notify Me 订阅弹窗
     */
    showNotifyMeModal(data) {
      const { product, variant, selectedSize, system } = data;

      // 绑定当前尺码选择器自己的通知按钮，避免多个商品互相覆盖
      const notifyButton = this.resolveNotifyButton(product);
      if (notifyButton) {
        // 格式化尺码显示：除了 CM 和 IN，其他类型在数值前添加类型前缀
        let formattedSize = selectedSize;
        const sizeSystem = system || this.state.currentSystem;

        if (this.productType === "FOOTWEAR" && sizeSystem) {
          // 检查是否是 CM 或 IN
          if (sizeSystem === "CM") {
            formattedSize = `${selectedSize} cm`;
          } else if (sizeSystem === "IN") {
            formattedSize = `${selectedSize}"`;
          } else {
            // 其他类型（US-M, US-W, EU, UK）：类型在前
            const statusSystemLabel = this.getStatusSystemLabel(sizeSystem);
            formattedSize = `${statusSystemLabel} ${selectedSize}`;
          }
        }

        // 尺码写在当前按钮上。换算结果优先，变体选项只作兜底。
        notifyButton.dataset.sizeFilterValue = this.buildSizeFilterValue(
          selectedSize,
          sizeSystem,
          variant,
        );
        notifyButton.dataset.variantId = variant.id;
        notifyButton.dataset.selectedSize = formattedSize;
        notifyButton.dataset.selectedSizeRaw = selectedSize;
        notifyButton.dataset.selectedSizeSystem = sizeSystem || "";
        notifyButton.dataset.productHandle = product.handle;
        notifyButton.dataset.productId = product.id;

        // 打开前写成当前变体。主题初始化时没有 base-url，会把地址拼成 undefined/variants//。
        const rawBase =
          notifyButton.getAttribute("data-base-url") || window.location.origin;
        const baseUrl = String(rawBase).replace(/\/+$/, "");
        notifyButton.setAttribute(
          "data-mfp-src",
          `${baseUrl}/variants/${variant.id}/?section_id=back-in-stock`,
        );
        notifyButton.setAttribute(
          "data-storageid",
          `notify-stock${variant.id}`,
        );
        notifyButton.click();

        // 弹窗打开后更新尺码显示
        setTimeout(() => {
          const sizeDisplay = document.querySelector("[data-size-display]");
          if (sizeDisplay) {
            sizeDisplay.textContent = ` - ${formattedSize}`;
          }
        }, 100);
      }
    }

    updateStatusText() {
      if (!this.elements.statusValue) return;

      if (this.state.selectedSize) {
        // 只有鞋类产品才显示尺码体系
        if (this.productType === "FOOTWEAR") {
          // CM 和 IN 单位显示在数值后面
          if (
            this.state.currentSystem === "CM" ||
            this.state.currentSystem === "IN"
          ) {
            this.elements.statusValue.textContent = `${this.state.selectedSize} ${this.state.currentSystem}`;
          } else {
            const statusSystemLabel = this.getStatusSystemLabel(
              this.state.currentSystem,
            );
            this.elements.statusValue.textContent = `${statusSystemLabel} ${this.state.selectedSize}`;
          }
        } else {
          // 非鞋类产品只显示尺码值
          this.elements.statusValue.textContent = this.state.selectedSize;
        }
      } else {
        this.elements.statusValue.textContent = "Please select a size";
      }
    }

    updateProductForm() {
      const productForm = document.querySelector(
        '[data-type="add-to-cart-form"]',
      );
      if (!productForm) {
        log.warn('[尺码选择器] 未找到产品表单 [data-type="add-to-cart-form"]');
        return;
      }

      const variantInput = productForm.querySelector(
        'select[name="id"], input[name="id"]',
      );
      if (!variantInput) {
        log.warn(
          '[尺码选择器] 未找到变体输入字段 select[name="id"], input[name="id"]',
        );
        return;
      }

      // 更新变体ID
      if (this.state.selectedVariant) {
        const variantId = this.state.selectedVariant.id;
        log.info("[尺码选择器] 更新变体ID:", variantId);

        if (variantInput.tagName === "SELECT") {
          variantInput.value = variantId;
          const options = variantInput.querySelectorAll("option");
          options.forEach((opt) => {
            opt.selected = parseInt(opt.value) === variantId;
          });
        } else {
          variantInput.value = variantId;
        }

        // ⚠️ 关键修复：确保表单识别到已选择的变体
        // 移除表单上可能存在的 data-disable-swatch 属性
        productForm.removeAttribute("data-disable-swatch");

        // ⚠️ 关键：确保主题识别到已选择的选项
        // 设置表单数据属性，标记已选择状态
        productForm.setAttribute("data-option-selected", "true");

        // 查找并更新隐藏的尺码 swatch 容器（如果存在）
        const swatchWrapper = productForm.querySelector(".t4s-swatch");
        if (swatchWrapper) {
          // 标记 swatch wrapper 为已选择
          swatchWrapper.setAttribute("data-has-selection", "true");
        }

        // 1. 查找尺码相关的 swatch option 容器
        const allSwatchOptions = productForm.querySelectorAll(
          "[data-swatch-option]",
        );
        let sizeSwatchOption = null;
        allSwatchOptions.forEach((option) => {
          const className = option.className || "";
          const optionTitle =
            option.querySelector(".t4s-swatch__title span")?.textContent || "";
          if (
            className.includes("size") ||
            className.includes("尺码") ||
            optionTitle.toLowerCase().includes("size") ||
            optionTitle.includes("尺码")
          ) {
            sizeSwatchOption = option;
          }
        });

        if (sizeSwatchOption) {
          // 标记这个 option 为已选择
          sizeSwatchOption.setAttribute("data-selected", "true");

          // 2. 更新尺码 swatch 的当前显示值
          const currentValueEl = sizeSwatchOption.querySelector(
            ".t4s-swatch__title [data-current-value]",
          );
          if (currentValueEl) {
            const displayValue =
              this.state.selectedVariant.option1 || this.state.selectedSize;
            currentValueEl.textContent = displayValue;
          }

          // 3. 创建虚拟的 swatch items（如果不存在）
          let swatchItems =
            sizeSwatchOption.querySelectorAll("[data-swatch-item]");
          if (swatchItems.length === 0) {
            // 如果没有 swatch items，创建一个隐藏的虚拟项
            const virtualItem = document.createElement("div");
            virtualItem.setAttribute("data-swatch-item", "");
            virtualItem.setAttribute(
              "data-value",
              this.state.selectedVariant.option1,
            );
            virtualItem.classList.add("is--selected");
            virtualItem.style.display = "none";
            sizeSwatchOption.appendChild(virtualItem);
          } else {
            // 标记对应的 swatch item 为选中
            swatchItems.forEach((item) => {
              item.classList.remove("is--selected");
              if (item.dataset.value === this.state.selectedVariant.option1) {
                item.classList.add("is--selected");
              }
            });
          }
        } else {
          // 创建一个虚拟的 swatch option 确保主题验证通过
          const virtualSwatchOption = document.createElement("div");
          virtualSwatchOption.setAttribute("data-swatch-option", "");
          virtualSwatchOption.setAttribute("data-selected", "true");
          virtualSwatchOption.style.display = "none";

          const virtualItem = document.createElement("div");
          virtualItem.setAttribute("data-swatch-item", "");
          virtualItem.setAttribute(
            "data-value",
            this.state.selectedVariant.option1,
          );
          virtualItem.classList.add("is--selected");
          virtualSwatchOption.appendChild(virtualItem);

          productForm.appendChild(virtualSwatchOption);
        }

        // 触发多个事件确保主题正确接收更新
        // 性能优化：使用事件批处理
        const inputEvent = new Event("input", { bubbles: true });
        const changeEvent = new Event("change", { bubbles: true });
        const customEvent = new CustomEvent("variant:change", {
          bubbles: true,
          detail: {
            variant: this.state.selectedVariant,
            variantId: variantId,
          },
        });

        variantInput.dispatchEvent(inputEvent);
        variantInput.dispatchEvent(changeEvent);
        variantInput.dispatchEvent(customEvent);

        // ⚠️ 关键：在事件冒泡和主题处理完成后，再次确认按钮状态
        // 某些主题的事件监听器可能会修改按钮状态，我们需要在之后重新设置
        setTimeout(() => {
          this.ensureButtonState();
        }, 50); // 50ms 延迟足以让事件处理完成
      }

      // 清理未选择状态时的虚拟元素
      if (!this.state.selectedVariant) {
        // 清理虚拟的 swatch 元素
        productForm.removeAttribute("data-option-selected");
        const swatchWrapper = productForm.querySelector(".t4s-swatch");
        if (swatchWrapper) {
          swatchWrapper.removeAttribute("data-has-selection");
        }

        // 清理 swatch items 的选中状态
        const swatchItems = productForm.querySelectorAll(
          "[data-swatch-item].is--selected",
        );
        swatchItems.forEach((item) => {
          item.classList.remove("is--selected");
        });

        // 移除虚拟的 swatch option
        const virtualOptions = productForm.querySelectorAll(
          '[data-swatch-option][style*="display: none"]',
        );
        virtualOptions.forEach((option) => {
          option.remove();
        });
      }

      // 更新按钮状态 - 立即更新，不等待事件处理
      const submitButton = productForm.querySelector("[data-atc-form]");
      if (!submitButton) {
        log.warn("[尺码选择器] 未找到提交按钮 [data-atc-form]");
        return;
      }

      const btnText = submitButton.querySelector(".t4s-btn-atc_text");

      // 性能优化：批量更新按钮属性
      if (this.state.selectedVariant && this.state.selectedVariant.available) {
        // 有可用的变体被选中 - 启用按钮
        submitButton.removeAttribute("disabled");
        submitButton.setAttribute("aria-disabled", "false");
        submitButton.classList.remove("is--disabled");
        if (btnText) btnText.textContent = "Add to Cart";
      } else if (this.state.selectedVariant) {
        // 有变体被选中但不可用 - 禁用按钮
        submitButton.setAttribute("disabled", "disabled");
        submitButton.setAttribute("aria-disabled", "true");
        submitButton.classList.add("is--disabled");
        if (btnText) btnText.textContent = "Sold Out";
      } else {
        // 没有选中任何变体 - 禁用按钮
        submitButton.setAttribute("disabled", "disabled");
        submitButton.setAttribute("aria-disabled", "true");
        submitButton.classList.add("is--disabled");
        if (btnText) btnText.textContent = "Select Options";
      }

      // 同步"Selected Size"行项属性，便于结账页显示单一尺码文案
      const sizeProp = productForm.querySelector("[data-size-display-prop]");
      if (sizeProp) {
        if (this.state.selectedSize) {
          sizeProp.value = `${this.state.selectedSize} ${this.state.currentSystem}`;
        } else {
          sizeProp.value = "";
        }
      }
    }

    /**
     * 确保按钮状态正确（在主题事件处理后调用）
     * 这个方法在延迟后执行，确保我们的按钮状态不会被主题的事件处理器覆盖
     */
    ensureButtonState() {
      const productForm = document.querySelector(
        '[data-type="add-to-cart-form"]',
      );
      if (!productForm) {
        log.warn("[尺码选择器] ensureButtonState: 未找到产品表单");
        return;
      }

      const submitButton = productForm.querySelector("[data-atc-form]");
      if (!submitButton) {
        log.warn("[尺码选择器] ensureButtonState: 未找到提交按钮");
        return;
      }

      const btnText = submitButton.querySelector(".t4s-btn-atc_text");

      // 根据当前状态设置按钮（性能优化：仅在必要时更新）
      if (this.state.selectedVariant && this.state.selectedVariant.available) {
        // 有可用的变体被选中 - 启用按钮
        const isDisabled = submitButton.hasAttribute("disabled");
        if (isDisabled) {
          submitButton.removeAttribute("disabled");
          submitButton.setAttribute("aria-disabled", "false");
          submitButton.classList.remove("is--disabled");
          if (btnText) btnText.textContent = "Add to Cart";
          log.info("[尺码选择器] 修正按钮状态为启用");
        }
      } else if (this.state.selectedVariant) {
        // 有变体被选中但不可用 - 禁用按钮
        submitButton.setAttribute("disabled", "disabled");
        submitButton.setAttribute("aria-disabled", "true");
        submitButton.classList.add("is--disabled");
        if (btnText) btnText.textContent = "Sold Out";
      } else {
        // 没有选中任何变体 - 禁用按钮
        submitButton.setAttribute("disabled", "disabled");
        submitButton.setAttribute("aria-disabled", "true");
        submitButton.classList.add("is--disabled");
        if (btnText) btnText.textContent = "Select Options";
      }
    }

    /**
     * 根据选择的尺码系统动态更新尺码按钮
     * @param {string} system - 尺码系统 (US-M, US-W, UK, EU, CM, IN)
     */
    updateSizeButtonsForSystem(system) {
      // 非鞋类产品不进行动态更新
      if (this.productType !== "FOOTWEAR") return;

      const activePanel = this.container.querySelector(
        `.t4s-size-selector__panel[data-size-panel="${system}"]`,
      );
      if (!activePanel) return;

      const grid = activePanel.querySelector("[data-size-grid]");
      if (!grid) return;

      // 清空现有按钮
      grid.innerHTML = "";

      // 创建新按钮
      this.data.variants.forEach((variant) => {
        if (!variant.convertedSizes || !variant.convertedSizes[system]) return;

        const size = variant.convertedSizes[system];
        const isAvailable = variant.available;
        const isSelected =
          this.state.selectedVariant &&
          this.state.selectedVariant.id === variant.id;

        // 创建按钮元素
        const button = document.createElement("button");
        button.type = "button";
        button.className = "t4s-size-selector__size-btn";
        button.dataset.sizeBtn = "";
        button.dataset.sizeValue = size;
        button.dataset.variantId = variant.id;
        button.dataset.available = isAvailable;

        if (isSelected) {
          button.classList.add("is-selected");
          // 🔧 修复：切换尺码类型时，更新 selectedSize 为新尺码体系下的值
          this.state.selectedSize = size;
          log.info("[尺码选择器] 切换尺码类型后更新显示:", size, system);
        }

        if (!isAvailable) {
          button.classList.add("is-out-of-stock");
          button.setAttribute("aria-disabled", "true");
        }

        // 创建标签
        const label = document.createElement("span");
        label.className = "t4s-size-selector__size-label";
        label.textContent = this.converter.formatSizeLabel(size, system);
        button.appendChild(label);

        // 如果缺货,添加图标
        if (!isAvailable) {
          const svg = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "svg",
          );
          svg.setAttribute("class", "t4s-size-selector__notify-icon");
          svg.setAttribute("width", "11");
          svg.setAttribute("height", "12");
          svg.setAttribute("viewBox", "0 0 11 12");
          svg.setAttribute("fill", "none");
          svg.innerHTML = `<g clip-path="url(#clip0_8124_1797)">
            <path d="M5.29723 11.9918C5.13391 11.9758 4.89528 11.8981 4.74276 11.8318C4.18195 11.5888 3.80652 11.09 3.65916 10.5029L1.32677 10.4935C0.9626 10.4691 0.63034 10.2088 0.536247 9.85541C0.346417 9.14384 1.00202 8.86062 1.35892 8.36886C2.29938 7.07252 1.85942 6.03405 2.02156 4.60456C2.20458 2.98978 3.51227 1.67842 5.13321 1.52131L5.13884 0.273824C5.23997 -0.0551912 5.70129 -0.104978 5.84043 0.217226C5.94016 0.448077 5.83598 1.47598 5.88314 1.52131C7.56696 1.67537 8.90656 3.1375 8.99854 4.81146C9.07386 6.18059 8.76671 7.18713 9.6572 8.39211C10.0178 8.88011 10.6735 9.1591 10.4564 9.87866C10.3503 10.2305 10.0284 10.4653 9.66564 10.4932L7.33771 10.4954C7.20467 11.1069 6.77175 11.6343 6.18677 11.8621C6.06452 11.9098 5.84442 11.9786 5.71889 11.9913C5.61095 12.0021 5.40563 12.0019 5.29746 11.9913L5.29723 11.9918ZM5.26907 2.25496C4.55598 2.3132 3.87527 2.66993 3.41161 3.20795C2.32261 4.4714 2.97516 5.88892 2.64384 7.33555C2.44674 8.19578 1.94201 8.98203 1.27163 9.54706C1.2247 9.61329 1.2538 9.68773 1.32161 9.72601L9.67597 9.73094C9.73862 9.68844 9.76959 9.63067 9.73486 9.55622C9.71257 9.50855 9.42982 9.2784 9.36013 9.2049C8.81458 8.62765 8.42788 7.84022 8.30164 7.05468C8.1456 6.08266 8.41568 4.98289 8.0846 4.06301C7.66575 2.90031 6.5073 2.15374 5.2693 2.25519L5.26907 2.25496ZM6.56197 10.4928H4.45438C4.78054 11.4873 6.21024 11.4826 6.56197 10.4928Z" fill="currentColor"/>
            <path d="M8.85083 0.567157C9.08078 0.524886 9.18097 0.636906 9.32622 0.783448C10.2566 1.72188 10.9206 3.25728 10.9894 4.57757C11.0051 4.87911 11.057 5.3401 10.6496 5.36429C10.1644 5.39294 10.2641 4.90447 10.2388 4.60223C10.1562 3.61096 9.8164 2.66102 9.23705 1.85644C9.04042 1.58332 8.80061 1.3567 8.59788 1.09039C8.51786 0.879029 8.61501 0.610603 8.85083 0.567157Z" fill="currentColor"/>
            <path d="M1.96518 0.566915C2.24769 0.508439 2.49478 0.739524 2.42391 1.02556C2.38285 1.19066 2.11394 1.41235 1.99568 1.55748C1.32577 2.3792 0.882753 3.37423 0.777397 4.43806C0.759094 4.62383 0.783028 5.0301 0.711696 5.16866C0.5695 5.44437 0.143851 5.42863 0.0328628 5.13555C-0.048325 4.92137 0.0415447 4.2523 0.0816693 3.99961C0.255308 2.90642 0.793353 1.75405 1.53812 0.935853C1.62095 0.844969 1.85912 0.588755 1.96518 0.566915Z" fill="currentColor"/>
            </g>
            <defs>
            <clipPath id="clip0_8124_1797">
            <rect width="11" height="12" fill="white"/>
            </clipPath>
            </defs>`;
          button.appendChild(svg);
        }

        // 添加点击事件
        button.addEventListener("click", (e) => this.handleSizeClick(e));

        grid.appendChild(button);
      });

      // 更新 elements 引用
      this.elements.sizeButtons =
        this.container.querySelectorAll("[data-size-btn]");
    }

    /**
     * 🛒 初始化购物车提交功能
     * 拦截Add to Cart按钮，使用AJAX提交
     */
    initCartSubmission() {
      log.info("[尺码选择器-购物车] 初始化购物车提交");

      // 获取产品表单
      const productForm = document.querySelector(
        '[data-type="add-to-cart-form"]',
      );
      if (!productForm) {
        log.warn("[尺码选择器-购物车] 未找到产品表单");
        return;
      }

      // 移除可能阻止提交的属性
      productForm.removeAttribute("data-disable-swatch");

      // 获取提交按钮
      const submitButton = productForm.querySelector("[data-atc-form]");
      if (!submitButton) {
        log.warn("[尺码选择器-购物车] 未找到提交按钮");
        return;
      }

      // 克隆按钮以移除所有原有事件监听器
      const newButton = submitButton.cloneNode(true);
      submitButton.parentNode.replaceChild(newButton, submitButton);

      // 添加新的点击事件
      newButton.addEventListener(
        "click",
        (e) => this.handleCartSubmit(e, productForm, newButton),
        true,
      );

      log.info("[尺码选择器-购物车] ✅ 购物车提交已拦截");
    }

    /**
     * 🛒 处理购物车提交
     */
    handleCartSubmit(e, form, button) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // 获取变体ID
      const variantInput = form.querySelector(
        'select[name="id"], input[name="id"]',
      );
      if (!variantInput || !variantInput.value) {
        log.warn("[购物车] 未选择变体");
        return;
      }

      // UI元素
      const btnText = button.querySelector(".t4s-btn-atc_text");
      const loadingSpinner = button.querySelector(".t4s-loading__spinner");
      const originalText = btnText ? btnText.textContent : "Add to Cart";

      // 显示加载状态
      button.disabled = true;
      button.classList.add("t4s-loading");
      if (btnText) btnText.textContent = "Adding...";
      if (loadingSpinner) loadingSpinner.removeAttribute("hidden");

      // 准备数据
      const variantId = variantInput.value;
      const quantity = form.querySelector('input[name="quantity"]')?.value || 1;

      // 存储用户选择的尺码类型到购物车 line item properties
      let properties = {};

      // 只对鞋类产品添加尺码类型信息
      if (
        this.productType === "FOOTWEAR" &&
        this.state.currentSystem &&
        this.state.selectedSize
      ) {
        // 格式化用户选择的尺码显示
        let formattedSize = this.state.selectedSize;
        const sizeSystem = this.state.currentSystem;

        if (sizeSystem === "CM") {
          formattedSize = `${this.state.selectedSize} cm`;
        } else if (sizeSystem === "IN") {
          formattedSize = `${this.state.selectedSize}"`;
        } else {
          // US-M, US-W, EU, UK
          const statusSystemLabel = this.getStatusSystemLabel(sizeSystem);
          formattedSize = `${statusSystemLabel} ${this.state.selectedSize}`;
        }

        // 使用下划线前缀使其在购物车页面隐藏，但在后端可读取
        properties["_selected_size"] = formattedSize;
      }

      log.info("[购物车] 提交数据:", { variantId, quantity, properties });

      // 使用AJAX提交到购物车
      fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: parseInt(quantity),
          properties: properties,
        }),
      })
        .then((response) => {
          if (!response.ok) throw new Error("Failed to add");
          return response.json();
        })
        .then((data) => {
          log.info("[购物车] ✅ 成功添加到购物车");

          // 恢复按钮
          button.disabled = false;
          button.classList.remove("t4s-loading");
          if (btnText) btnText.textContent = originalText;
          if (loadingSpinner) loadingSpinner.setAttribute("hidden", "");

          if(window.Cart.refreshCartSection) window.Cart.refreshCartSection()

          // 延迟后打开购物车（给主题时间响应事件）
          setTimeout(() => {
            this.openCart();
          }, 800);
        })
        .catch((error) => {
          log.error("[购物车] ❌ 添加失败:", error);

          // 恢复按钮
          button.disabled = false;
          button.classList.remove("t4s-loading");
          if (btnText) btnText.textContent = originalText;
          if (loadingSpinner) loadingSpinner.setAttribute("hidden", "");
        });
    }

    /**
     * 🛒 打开购物车侧边栏
     */
    openCart() {
      log.info("[购物车] 打开购物车");

      // 查找购物车触发器
      const cartTrigger = document.querySelector(
        '[data-drawer-options*="mini_cart"], a[href="/cart"][data-drawer-options]',
      );

      if (cartTrigger) {
        log.info("[购物车] 点击购物车图标");
        cartTrigger.click();
      } else {
        log.warn("[购物车] 未找到购物车触发器");

        // 备用：尝试通过主题API打开
        if (window.T4SThemeSP && window.T4SThemeSP.Drawer) {
          log.info("[购物车] 使用主题Drawer API");
          if (typeof window.T4SThemeSP.Drawer.open === "function") {
            window.T4SThemeSP.Drawer.open("#t4s-mini_cart");
          }
        }
      }
    }
  }

  // 初始化
  function initSizeSelectors() {
    const selectors = document.querySelectorAll("[data-size-selector]");
    selectors.forEach((container) => {
      if (container.dataset.initialized === "true") return;
      new SizeSelector(container);
      container.dataset.initialized = "true";
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initSizeSelectors);
  } else {
    initSizeSelectors();
  }

  // 支持动态内容
  if (typeof MutationObserver !== "undefined") {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.matches && node.matches("[data-size-selector]")) {
              new SizeSelector(node);
              node.dataset.initialized = "true";
            }
            const childSelectors =
              node.querySelectorAll &&
              node.querySelectorAll("[data-size-selector]");
            if (childSelectors) {
              childSelectors.forEach((container) => {
                if (container.dataset.initialized !== "true") {
                  new SizeSelector(container);
                  container.dataset.initialized = "true";
                }
              });
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  window.SizeSelector = SizeSelector;
})();
